/**
 * Main Application Component
 * 
 * This is the root component of the resume website application.
 * It handles lazy loading and overall layout structure using a responsive grid system.
 */

import React, { lazy, Suspense, useState, useEffect } from 'react';
import './App.css';
// Import Google Fonts - Press Start 2P for 16-bit style monogram
import '@fontsource/press-start-2p';
import { ThemeProvider, CssBaseline } from '@mui/material';
import darkTheme from './theme';

// Lazy load components for better performance and code splitting
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollProgress from './components/ScrollProgress';

// Layout imports
import { PageLayout } from './components/layout';

/**
 * Lazy load components to improve initial loading time and enable code splitting
 * This optimization prevents heavy components from blocking the initial render
 */
const ParticlesBackground = lazy(() => import('./components/Particles'));
const About = lazy(() => import('./components/About'));
const Certifications = lazy(() => import('./components/Certifications'));
const AICollaboration = lazy(() => import('./components/AICollaboration'));
const Contact = lazy(() => import('./components/Contact'));

/**
 * App component that serves as the root of the application
 * It configures the Material UI theme provider, sets up lazy loading,
 * and organizes the main content sections in a responsive grid layout.
 */
const App = () => {
  // State to track if the initial loading animation is complete
  const [isLoading, setIsLoading] = useState(true);

  // Simulate loading time for the initial animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // 2 seconds loading screen

    return () => clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />

      {/* Always render content so backdrop-filter initializes during loading screen */}
      <PageLayout
        header={<Header />}
        footer={<Footer />}
      >
        <ErrorBoundary fallbackMessage="Unable to load background animations. The page will continue to work without them.">
          <Suspense fallback={<div>Loading particles...</div>}>
            <ParticlesBackground />
          </Suspense>
        </ErrorBoundary>

        {/* Main content sections with lazy loading */}
        <ErrorBoundary fallbackMessage="Unable to load about section">
          <Suspense fallback={<div>Loading about section...</div>}>
            <About />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary fallbackMessage="Unable to load certifications">
          <Suspense fallback={<div>Loading certifications...</div>}>
            <Certifications />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary fallbackMessage="Unable to load AI collaboration section">
          <Suspense fallback={<div>Loading AI collaboration...</div>}>
            <AICollaboration />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary fallbackMessage="Unable to load contact form">
          <Suspense fallback={<div>Loading contact form...</div>}>
            <Contact />
          </Suspense>
        </ErrorBoundary>
      </PageLayout>

      {/* Show loading screen overlay while initializing */}
      {isLoading && <LoadingScreen />}
      {!isLoading && <ScrollProgress />}
    </ThemeProvider>
  );
};

export default App;
